import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';

import { Container, Row, Col, Button } from 'react-bootstrap';
import { FaTrashAlt } from 'react-icons/fa';

import { useToast } from '../../hooks/toast';
import api from '../../services/api';

import { formatPrice } from '../../utils/funcoes';

import SelectAsync from '../SelectAsync';
import Loading from '../Loading';
import getResponseErrors from '../../utils/getResponseErrors';
import { useClinica } from '../../hooks/clinica';

type ComponentProps = {
  exibeBusca?: boolean;
  procedimentos?: number[];
};

interface IProcedimento {
  ProCodigo: number;
  ProNome: string;
  ProValor: number;
  ProValorPlano: number;
  ProValorPlano2: number;
  ProValorFormatado: string;
  ProValorPlanoFormatado: string;
  ProValorPlano2Formatado: string;
  ProValorPlanoEmpresarial: number;
  ProValorPlanoEmpresarialFormatado: string;
  ProCarencia: number;
  perfil: { PerNome: string };
}

const OrcamentoProcedimento: React.FC<ComponentProps> = ({
  exibeBusca,
  procedimentos,
}) => {
  const { addToast } = useToast();

  const { imagensClinica } = useClinica();

  const cliSaoPaulo = !!imagensClinica.find(img => img.nome === 'saopaulo');

  const [loading, SetLoading] = useState(false);
  const formRefBusca = useRef<FormHandles>(null);
  const [procedimentosExibir, setProcedimentosExibir] = useState(procedimentos);
  const [procedimentosFormatados, setProcedimentosFormatados] = useState<
    IProcedimento[]
  >();

  const handleBuscaProcedimento = useCallback(
    async codigo => {
      SetLoading(true);
      try {
        const response = await api.get<IProcedimento>(
          `/procedimentos/${codigo}`,
        );

        const procedFormatados = {
          ...response.data,
          ProNome: `${response.data.perfil.PerNome} - ${response.data.ProNome}`,
          ProValorFormatado: formatPrice(response.data.ProValor),
          ProValorPlanoFormatado: formatPrice(response.data.ProValorPlano),
          ProValorPlano2Formatado: formatPrice(response.data.ProValorPlano2),
          ProValorPlanoEmpresarialFormatado: formatPrice(
            response.data.ProValorPlanoEmpresarial,
          ),
        };

        setProcedimentosFormatados(state => {
          return state ? state.concat([procedFormatados]) : [procedFormatados];
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar o agendamento',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        SetLoading(false);
      }
    },
    [addToast],
  );

  const handleRemoveProcedimento = useCallback((ProCodigo: Number) => {
    setProcedimentosFormatados(state => {
      if (state) return state.filter(pro => pro.ProCodigo !== ProCodigo);
      return [];
    });
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      SetLoading(true);
      try {
        const response = await api.get<IProcedimento[]>(
          `/procedimentos/porcodigos?codigos=${procedimentosExibir?.join()}`,
        );
        const procedFormatados = response.data.map(procedimento => {
          return {
            ...procedimento,
            ProNome: `${procedimento.perfil.PerNome} - ${procedimento.ProNome}`,
            ProValorFormatado: formatPrice(procedimento.ProValor),
            ProValorPlanoFormatado: formatPrice(procedimento.ProValorPlano),
            ProValorPlano2Formatado: formatPrice(procedimento.ProValorPlano2),
            ProValorPlanoEmpresarialFormatado: formatPrice(
              procedimento.ProValorPlanoEmpresarial,
            ),
          };
        });

        setProcedimentosFormatados(state => {
          return state ? state.concat(procedFormatados) : procedFormatados;
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar o agendamento',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        SetLoading(false);
      }
    }
    if (procedimentosExibir && procedimentosExibir.length > 0) loadDados();
  }, [procedimentosExibir, addToast]);

  const handleSelectProcedimentoChange = useCallback(
    ({ value, label }) => {
      if (value) {
        handleBuscaProcedimento(value);
      }
    },
    [handleBuscaProcedimento],
  );

  const totais = useMemo(() => {
    if (!procedimentosFormatados)
      return { valorParticular: '0', valorCartao: '0', valorFamiliar: '0' };
    const valorParticular = procedimentosFormatados.reduce((tot, prod) => {
      return tot + Number(prod.ProValor);
    }, 0);
    console.log('total Particular: ', valorParticular);
    const valorCartao = procedimentosFormatados.reduce((tot, prod) => {
      return (
        tot +
        Number(prod.ProCarencia > 1 ? prod.ProValorPlano2 : prod.ProValorPlano)
      );
    }, 0);
    console.log('total cartão: ', valorCartao);

    const valorFamiliar = procedimentosFormatados.reduce((tot, prod) => {
      return tot + Number(prod.ProValorPlanoEmpresarial);
    }, 0);
    console.log('total familiar: ', valorFamiliar);

    const total = {
      valorParticular: formatPrice(valorParticular),
      valorCartao: formatPrice(valorCartao),
      valorFamiliar: formatPrice(valorFamiliar),
    };

    return total;
  }, [procedimentosFormatados]);

  return (
    <Container style={{ height: '100%', minHeight: '400px' }}>
      {loading && <Loading />}
      <h3>Valor Particular X Associado</h3>
      {exibeBusca && (
        <Form ref={formRefBusca} onSubmit={() => console.log('nada')}>
          <SelectAsync
            name="procedimentoBusca"
            label="Procedimento"
            urlApi="procedimentos/combo?termoBusca="
            placeholder="Digite para buscar"
            handleSelectChange={handleSelectProcedimentoChange}
          />
        </Form>
      )}
      {procedimentosFormatados && (
        <>
          <Row className="border-bottom striped p-2 bg-light mt-2" noGutters>
            <Col>
              <b>Procedimento</b>
            </Col>
            <Col sm={2}>
              <b>Particular</b>
            </Col>
            <Col sm={2}>
              <b>Cartão Saúde</b>
            </Col>
            {cliSaoPaulo && (
              <Col sm={2}>
                <b>Cartão Familiar</b>
              </Col>
            )}
            <Col sm={1} />
          </Row>
          {procedimentosFormatados.map(proced => (
            <Row
              key={`proced-${proced.ProCodigo}`}
              className="border-bottom striped p-2"
              noGutters
            >
              <Col>
                {proced.ProNome}{' '}
                {proced.ProCarencia > 1 && (
                  <small>
                    * a partir da {proced.ProCarencia}º mensalidade{' '}
                    {proced.ProValorPlanoFormatado}
                  </small>
                )}
              </Col>
              <Col sm={2}>{proced.ProValorFormatado}</Col>
              <Col sm={2}>
                {proced.ProCarencia > 1
                  ? proced.ProValorPlano2Formatado
                  : proced.ProValorPlanoFormatado}
              </Col>
              {cliSaoPaulo && (
                <Col sm={2}>{proced.ProValorPlanoEmpresarialFormatado}</Col>
              )}
              <Col sm={1}>
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  className="py-0"
                  onClick={() => {
                    handleRemoveProcedimento(proced.ProCodigo);
                  }}
                >
                  <FaTrashAlt />
                </Button>
              </Col>
            </Row>
          ))}
          <Row className="border-bottom p-2 bg-light" noGutters>
            <Col>
              <b>Total</b>
            </Col>
            <Col sm={2}>
              <b>{totais.valorParticular}</b>
            </Col>
            <Col sm={2}>
              <b>{totais.valorCartao}</b>
            </Col>
            {cliSaoPaulo && (
              <Col sm={2}>
                <b>{totais.valorFamiliar}</b>
              </Col>
            )}
            <Col sm={1} />
          </Row>
        </>
      )}
    </Container>
  );
};

export default OrcamentoProcedimento;
